import {type FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {OttopilotEventStatus, type OttopilotEvent} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {OttopilotEventContainerStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {OttopilotEventContainerTemplateConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTemplateConfiguration";
import {
    EmotoradEventTypes,
    EmotoradLeadEventStatusEnum,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    emotoradLeadEventStatuses,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {amaronArchiveEventContainerAction, amaronArchiveEventAction} from "~/utilities/apis/amaronActionsApi";
import {isDuluxWorkspace} from "~/domainModels/workspaceSpecific/dulux";
import {duluxArchiveEventAction, duluxArchiveEventContainerAction} from "~/utilities/apis/duluxActionsApi";
import {getEventsWithEventContainers, updateEvent, type EventWithEventContainer, type EventContainerWithEvents} from "~/utilities/apis/api";
import {updateEventContainer} from "~/utilities/apis/eventContainersApi";
import type {WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Shared with events module data viewer — used to refresh event lists after Event Container archive. */
export const EVENTS_MODULE_DATA_VIEWER_QUERY_KEY = "7ca3b55c-03c6-431d-a657-81c6c9a35fe7" as Uuid;

/** Shared with Event Containers module data viewer — used to refresh Event Container lists after event archive. */
export const EVENT_CONTAINERS_MODULE_DATA_VIEWER_QUERY_KEY = "efadf6c4-78b9-4e6e-b474-0f8c46f661bf" as Uuid;

/** Journey profile Events tab data viewer (`EventsTab`). */
export const JOURNEY_EVENTS_MODULE_DATA_VIEWER_QUERY_KEY = "9458b914-bf84-423a-a020-241459b6818b" as Uuid;

export function buildEventContainerArchiveUpdatePayload(
    statusColumnConfig: NonNullable<OttopilotEventContainerTemplateConfiguration["statusColumnConfig"]>,
): Record<string, unknown> {
    return {
        status: OttopilotEventContainerStatus.archived,
        [statusColumnConfig.columnId]: statusColumnConfig.archiveStatusValue,
    };
}

export function buildEventArchiveUpdatePayload(event: Pick<OttopilotEvent, "templateId">, eventTemplates: ReadonlyArray<OttopilotEventTemplate>): Record<string, unknown> {
    const payload: Record<string, unknown> = {
        status: OttopilotEventStatus.archived,
    };

    const eventTemplate = eventTemplates.find((template) => template.id === event.templateId);
    const statusColumnConfig = eventTemplate?.configuration.statusColumnConfig;

    if (statusColumnConfig !== undefined) {
        payload[statusColumnConfig.columnId] = statusColumnConfig.archiveStatusValue;
    }

    if (event.templateId === (EmotoradEventTypes.lead as Uuid)) {
        payload[EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID] = emotoradLeadEventStatuses[EmotoradLeadEventStatusEnum.ARCHIVE].id;
    }

    return payload;
}

async function fetchEventContainerEvents(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    eventContainerId: Uuid;
}): Promise<Result<Array<Pick<OttopilotEvent, "id" | "templateId" | "status">>>> {
    const eventsResult = await getEventsWithEventContainers({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.workspaceId,
        filterConfiguration: {
            revisionId: generateUuid(),
            lineItems: [
                {
                    columnId: "eventContainerId",
                    operation: FilterOperation.equals,
                    value: args.eventContainerId,
                },
            ],
        } satisfies FilterConfiguration,
        sortConfiguration: {
            revisionId: generateUuid(),
            lineItems: [],
        },
        limit: 500,
        offset: 0,
    });

    if (eventsResult.success !== true) {
        return eventsResult;
    }

    return dataResult(
        eventsResult.data.map((event) => ({
            id: event.id,
            templateId: event.templateId,
            status: event.status,
        })),
    );
}

/**
 * Archives an Event Container and cascades archive status to all active events on it.
 */
export async function archiveEventContainerWithEvents(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    workspace?: {displayName: string};
    workspaceCapabilities?: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>;
    eventContainer: EventContainerWithEvents;
    statusColumnConfig: NonNullable<OttopilotEventContainerTemplateConfiguration["statusColumnConfig"]>;
    eventTemplates: ReadonlyArray<OttopilotEventTemplate>;
}): Promise<Result<void>> {
    if (args.workspaceCapabilities !== undefined && isAmaronWorkspace(args.workspaceCapabilities) === true) {
        const archiveResult = await amaronArchiveEventContainerAction({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            request: {
                workspaceId: args.workspaceId,
                eventContainerId: args.eventContainer.id,
            },
        });
        if (archiveResult.success !== true) {
            return errorResult(archiveResult.error);
        }
        return dataResult(undefined);
    }
    if (args.workspaceCapabilities !== undefined && isDuluxWorkspace(args.workspaceCapabilities) === true) {
        const archiveResult = await duluxArchiveEventContainerAction({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            request: {
                workspaceId: args.workspaceId,
                eventContainerId: args.eventContainer.id,
            },
        });
        if (archiveResult.success !== true) {
            return errorResult(archiveResult.error);
        }
        return dataResult(undefined);
    }

    const eventContainerUpdateResult = await updateEventContainer({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        id: args.eventContainer.id,
        templateId: args.eventContainer.templateId,
        workspaceId: args.workspaceId,
        data: buildEventContainerArchiveUpdatePayload(args.statusColumnConfig),
    });

    if (eventContainerUpdateResult.success !== true) {
        return eventContainerUpdateResult;
    }

    const eventsFromEventContainer =
        args.eventContainer.events.length > 0
            ? args.eventContainer.events.map((event) => ({
                  id: event.id,
                  templateId: event.templateId,
                  status: event.status,
              }))
            : null;

    const eventsResult =
        eventsFromEventContainer !== null
            ? dataResult(eventsFromEventContainer)
            : await fetchEventContainerEvents({
                  accessToken: args.accessToken,
                  intellsysBaseUrl: args.intellsysBaseUrl,
                  workspaceId: args.workspaceId,
                  eventContainerId: args.eventContainer.id,
              });

    if (eventsResult.success !== true) {
        return eventsResult;
    }

    for (const event of eventsResult.data) {
        if (event.status === OttopilotEventStatus.archived) {
            continue;
        }

        const eventUpdateResult = await updateEvent({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            eventId: event.id,
            templateId: event.templateId,
            workspaceId: args.workspaceId,
            data: buildEventArchiveUpdatePayload(event, args.eventTemplates) as never,
        });

        if (eventUpdateResult.success !== true) {
            return errorResult(
                new Error(eventUpdateResult.error instanceof Error ? eventUpdateResult.error.message : "Could not archive one or more events on this Event Container."),
            );
        }
    }

    return dataResult(undefined);
}

export function isEventDataViewerRow(row: Record<string, unknown>): boolean {
    return typeof row.templateId === "string" && UUID_PATTERN.test(row.templateId);
}

export function resolveEventTemplateIdFromDataViewerRow(row: Record<string, unknown>, queryKeyPrefix: Array<unknown>): Uuid | null {
    if (typeof row.templateId === "string" && UUID_PATTERN.test(row.templateId)) {
        return row.templateId as Uuid;
    }

    if (
        String(queryKeyPrefix[0]) === String(EVENTS_MODULE_DATA_VIEWER_QUERY_KEY) &&
        typeof queryKeyPrefix[3] === "string" &&
        UUID_PATTERN.test(queryKeyPrefix[3])
    ) {
        return queryKeyPrefix[3] as Uuid;
    }

    for (let index = queryKeyPrefix.length - 1; index >= 0; index -= 1) {
        const segment = queryKeyPrefix[index];
        if (typeof segment === "string" && UUID_PATTERN.test(segment)) {
            return segment as Uuid;
        }
    }

    return null;
}

function toEventContainerWithEventsForArchive(row: Record<string, unknown> & {id: Uuid}): EventContainerWithEvents {
    const eventContainerRow = row as Partial<EventContainerWithEvents>;
    return {
        ...(eventContainerRow as EventContainerWithEvents),
        id: row.id,
        events: Array.isArray(eventContainerRow.events) ? eventContainerRow.events : [],
    };
}

/** Same archive API + payload as the row ⋯ menu Archive action for events. */
export async function archiveEventLikeArchiveDropdown(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    workspace?: {displayName: string};
    workspaceCapabilities?: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>;
    event: EventWithEventContainer;
    eventTemplates: ReadonlyArray<OttopilotEventTemplate> | null;
}): Promise<Result<void>> {
    if (args.workspaceCapabilities !== undefined && isAmaronWorkspace(args.workspaceCapabilities) === true) {
        const archiveResult = await amaronArchiveEventAction({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            request: {
                workspaceId: args.workspaceId,
                eventId: args.event.id,
                templateId: args.event.templateId,
            },
        });
        if (archiveResult.success !== true) {
            return errorResult(archiveResult.error);
        }
        return dataResult(undefined);
    }

    if (args.workspaceCapabilities !== undefined && isDuluxWorkspace(args.workspaceCapabilities) === true) {
        const archiveResult = await duluxArchiveEventAction({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            request: {
                workspaceId: args.workspaceId,
                eventId: args.event.id,
                templateId: args.event.templateId,
            },
        });
        if (archiveResult.success !== true) {
            return errorResult(archiveResult.error);
        }
        return dataResult(undefined);
    }

    if (args.eventTemplates === null) {
        return errorResult(new Error("OttopilotEvent templates not found"));
    }

    const eventTemplate = args.eventTemplates.find((template) => template.id === args.event.templateId);
    if (eventTemplate?.configuration.statusColumnConfig === undefined) {
        return errorResult(new Error("Status column configuration not found for this event type"));
    }

    return updateEvent({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        eventId: args.event.id,
        templateId: args.event.templateId,
        workspaceId: args.workspaceId,
        data: buildEventArchiveUpdatePayload(args.event, args.eventTemplates) as never,
    });
}

/** Same archive API + payload as the row menu Archive action for Event Containers. */
export async function archiveEventContainerLikeArchiveDropdown(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    workspace?: {displayName: string};
    workspaceCapabilities?: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>;
    eventContainer: EventContainerWithEvents;
    statusColumnConfig: OttopilotEventContainerTemplateConfiguration["statusColumnConfig"];
    eventTemplates: ReadonlyArray<OttopilotEventTemplate> | null;
}): Promise<Result<void>> {
    if (args.workspaceCapabilities !== undefined && isDuluxWorkspace(args.workspaceCapabilities) === true) {
        const archiveResult = await duluxArchiveEventContainerAction({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            request: {
                workspaceId: args.workspaceId,
                eventContainerId: args.eventContainer.id,
            },
        });
        if (archiveResult.success !== true) {
            return errorResult(archiveResult.error);
        }
        return dataResult(undefined);
    }

    if (args.statusColumnConfig === undefined) {
        return errorResult(new Error("Status column configuration not found for Event Containers"));
    }

    const isAmaron = args.workspaceCapabilities !== undefined && isAmaronWorkspace(args.workspaceCapabilities) === true;
    if (isAmaron !== true && args.eventTemplates === null) {
        return errorResult(new Error("Events module configuration not found"));
    }

    return archiveEventContainerWithEvents({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.workspaceId,
        workspace: args.workspace,
        workspaceCapabilities: args.workspaceCapabilities,
        eventContainer: args.eventContainer,
        statusColumnConfig: args.statusColumnConfig,
        eventTemplates: args.eventTemplates ?? [],
    });
}

/**
 * Archives a data-viewer row (table/list bulk action) using the same logic as ⋯ → Archive.
 */
export async function archiveDataViewerRow<T extends {id: Uuid}>(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    workspace?: {displayName: string};
    workspaceCapabilities?: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>;
    row: T;
    queryKeyPrefix: Array<unknown>;
    eventContainerStatusColumnConfig: OttopilotEventContainerTemplateConfiguration["statusColumnConfig"];
    eventTemplates: ReadonlyArray<OttopilotEventTemplate> | null;
}): Promise<Result<void>> {
    const rowRecord = args.row as Record<string, unknown> & {id: Uuid};

    if (isEventDataViewerRow(rowRecord)) {
        const templateId = resolveEventTemplateIdFromDataViewerRow(rowRecord, args.queryKeyPrefix);
        if (templateId === null) {
            return errorResult(new Error("OttopilotEvent template id is missing for event archive."));
        }

        return archiveEventLikeArchiveDropdown({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            workspaceId: args.workspaceId,
            workspace: args.workspace,
            workspaceCapabilities: args.workspaceCapabilities,
            event: {
                ...(args.row as unknown as EventWithEventContainer),
                templateId,
            },
            eventTemplates: args.eventTemplates,
        });
    }

    return archiveEventContainerLikeArchiveDropdown({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.workspaceId,
        workspace: args.workspace,
        workspaceCapabilities: args.workspaceCapabilities,
        eventContainer: toEventContainerWithEventsForArchive(rowRecord),
        statusColumnConfig: args.eventContainerStatusColumnConfig,
        eventTemplates: args.eventTemplates,
    });
}
